import { useRef } from 'react';
import Loader from '@/components/Loader';
import { DataTable } from '@acast-tech/decibel';
import { ColumnHeader } from '@/components/tables/ColumnHeader';
import { Avatar } from '@acast-tech/decibel';
import { formatCurrency } from '@/utils/currencyUtils';
import { uppercaseFirstLetter } from '@/utils/stringUtils';
import { formatNumber, formatPercentage } from '@/utils/numberFormattingUtils';
import type { DecibelTableColumn } from '@/types';
import type { NetworkShow } from '@/types/api/network';
import useBreakpoint from '@/hooks/useBreakpoint';
import NoDataMessage from '@/components/states/NoDataMessage';
import { TotalImpressionsTooltip } from '@/components/tooltips/ImpressionsTooltips';
import type { PageType } from '@/types/api';

export const ShowPerformanceTable = ({
  data,
  isLoading = false,
  type = 'earnings',
}: {
  data: NetworkShow[] | undefined;
  isLoading?: boolean;
  type?: PageType;
}) => {
  const { isMobile, isTablet } = useBreakpoint();
  const popoverRef = useRef<HTMLDivElement>(null);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[25dvh]">
        <Loader />
      </div>
    );
  }

  if (!data) {
    return <NoDataMessage dataType={type} />;
  }

  const tableColumns = createTableColumns(popoverRef, type, isMobile, isTablet);

  return (
    <DataTable
      title="shows"
      columns={tableColumns}
      fixedLayout
      data={data}
      getRowId={(row) => row.id}
      pagination={{
        summary: true,
        pageSizeOptions: [5, 10, 15, 20],
        defaultPageSize: type === 'payouts' ? 5 : 10,
      }}
    />
  );
};

const createTableColumns = (
  popoverRef: React.RefObject<HTMLDivElement>,
  type: PageType,
  isMobile: boolean,
  isTablet: boolean
): DecibelTableColumn<NetworkShow>[] => {
  return [
    {
      id: 'name',
      title: <ColumnHeader>Show name</ColumnHeader>,
      sorter: ({ name }: NetworkShow) => name,
      width: isMobile ? '175px' : isTablet ? '200px' : '250px',
      render: ({ image_url, name }: NetworkShow) => {
        return (
          <div className="flex items-center gap-4">
            <Avatar
              variant="resource"
              alt={`${name} Thumbnail`}
              size="medium"
              src={image_url ?? undefined}
              data-testid={`show-thumbnail`}
            />
            <span className="text-xs md:text-sm font-medium text-textDarkGrey w-[100px] md:w-[200px] whitespace-normal line-clamp-2">
              {name}
            </span>
          </div>
        );
      },
    },
    {
      id: 'impressions',
      title: <TotalImpressionsTooltip popoverRef={popoverRef} />,
      sorter: (item: NetworkShow) => Number(item.impressions),
      align: 'right',
      width: '125px',
      render: ({ impressions }: NetworkShow) => (
        <span className="text-textDarkGrey">{formatNumber('en', impressions || 0)}</span>
      ),
    },
    ...(['ads', 'programmatic', 'sponsorships', 'branded_content', 'social', 'other'] as const).map(
      (key) => ({
        id: key,
        title: <ColumnHeader>{uppercaseFirstLetter(key.split('_')[0])}</ColumnHeader>,
        sorter: ({ revenue }: NetworkShow) => revenue[key].amount,
        align: 'right' as const,
        width: '130px',
        render: ({ revenue }: NetworkShow) => (
          <span className="font-light text-textDarkGrey">
            {formatCurrency(revenue[key].amount, revenue[key].currency)}
          </span>
        ),
      })
    ),
    {
      id: 'total',
      title: (
        <ColumnHeader>{type === 'payouts' ? 'Total revenue' : 'Total est. earnings'}</ColumnHeader>
      ),
      sorter: ({ revenue }: NetworkShow) => revenue.total.amount,
      align: 'right',
      width: '150px',
      render: ({ revenue }: NetworkShow) => (
        <span className="font-bold text-textDarkGrey">
          {formatCurrency(revenue.total.amount, revenue.total.currency)}
        </span>
      ),
    },
    {
      id: 'percentage',
      title: <ColumnHeader>% of total</ColumnHeader>,
      sorter: false,
      align: 'right',
      width: '100px',
      render: ({ id, percentage }: NetworkShow) => {
        return (
          <span className="font-light text-textMedGrey" data-testid={`percentage-value-${id}`}>
            {formatPercentage(percentage, { round: false, decimals: 2 })}%
          </span>
        );
      },
    },
  ];
};
